import { create } from 'zustand';
import { api } from '../lib/api';
import { useAuthStore, sessionVenueKey } from './auth.store';
import type { Edition, FeatureKey, EditionInfoDto, TierPolicy } from '@pokerroom/types';

interface EditionState {
  edition: Edition;
  tierPolicy: TierPolicy;
  enabledFeatures: FeatureKey[];
  isLoaded: boolean;
  isTrialActive: boolean;
  trialDaysRemaining: number | null;
  fetchEditionInfo: () => Promise<void>;
  hasFeature: (feature: FeatureKey) => boolean;
  isLite: () => boolean;
}
const empty = () => ({
  edition: 'business' as Edition,
  tierPolicy: 'EDITION' as TierPolicy,
  enabledFeatures: [] as FeatureKey[],
  isLoaded: false,
  isTrialActive: false,
  trialDaysRemaining: null,
});
let generation = 0;
let pending: AbortController | undefined;
const identity = () => {
  const s = useAuthStore.getState();
  return JSON.stringify([s.user?.id, s.sessionRevision, sessionVenueKey(s.accessToken)]);
};
export const useEditionStore = create<EditionState>((set, get) => ({
  ...empty(),
  fetchEditionInfo: async () => {
    const request = ++generation;
    const actor = identity();
    pending?.abort();
    const controller = new AbortController();
    pending = controller;
    try {
      const data = await api.get<EditionInfoDto>('/auth/edition-info', {
        signal: controller.signal,
      });
      if (request !== generation || actor !== identity()) return;
      const tierPolicy = data.tierPolicy ?? 'EDITION';
      set({
        edition: data.edition,
        tierPolicy,
        enabledFeatures: data.enabledFeatures,
        isLoaded: true,
        isTrialActive: tierPolicy === 'EDITION' && (data.isTrialActive ?? false),
        trialDaysRemaining: tierPolicy === 'EDITION' ? (data.trialDaysRemaining ?? null) : null,
      });
    } catch {
      if (request !== generation || actor !== identity()) return;
      if (!get().isLoaded) set({ ...empty(), isLoaded: true });
    } finally {
      if (pending === controller) pending = undefined;
    }
  },
  hasFeature: (feature) => get().enabledFeatures.includes(feature),
  isLite: () => get().tierPolicy === 'EDITION' && get().edition === 'lite',
}));

useAuthStore.subscribe((state, previous) => {
  if (
    state.user?.id === previous.user?.id &&
    state.sessionRevision === previous.sessionRevision &&
    sessionVenueKey(state.accessToken) === sessionVenueKey(previous.accessToken)
  )
    return;
  ++generation;
  pending?.abort();
  pending = undefined;
  useEditionStore.setState(empty());
  if (state.accessToken) void useEditionStore.getState().fetchEditionInfo();
});
